/**
 * ProfileCompletionCard Component
 *
 * Dashboard widget that encourages users to finish setting up their profile.
 * Displays a progress bar (color-coded red/yellow/green) and a checklist of
 * incomplete steps such as adding a name, bio, photo, mentor settings, or
 * organization affiliations. Each step links directly to the relevant
 * profile section. The card automatically hides once the profile reaches
 * 100% completion.
 *
 * @param {Object}   profileCompletion - Object with `percentage` (number) and `missing` (string[])
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, ProgressBar, ListGroup, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * Configuration for each profile completion step.
 * Keys correspond to the strings returned in profileCompletion.missing.
 * Each entry defines the display label, helper description, and navigation target.
 */
const STEP_CONFIG = {
    name: {
        label: 'Add your name',
        description: 'Let the ecosystem know who you are.',
        link: '/profile',
        linkText: 'Add Name',
    },
    bio: {
        label: 'Write a short bio',
        description: 'Tell the community about yourself and your interests.',
        link: '/profile',
        linkText: 'Add Bio',
    },
    photo: {
        label: 'Upload a profile photo',
        description: 'A photo helps others recognize you.',
        link: '/profile',
        linkText: 'Upload Photo',
    },
    mentor: {
        label: 'Set up your mentor profile',
        description:
            'Share your expertise or indicate whether you want to mentor others.',
        link: '/profile#mentor',
        linkText: 'Mentor Settings',
    },
    mentorDetail: {
        label: 'Complete your mentor details',
        description: 'Add areas of expertise, availability, and a mentor bio.',
        link: '/profile#mentor',
        linkText: 'Add Details',
    },
    affiliations: {
        label: 'Connect with an organization',
        description:
            'Link yourself to organizations you work with or are interested in.',
        link: '/profile#affiliations',
        linkText: 'Add Affiliation',
    },
};

/** Maps completion percentage to a Bootstrap color variant for the progress bar. */
function getProgressVariant(percentage) {
    if (percentage < 40) return 'danger';
    if (percentage < 70) return 'warning';
    return 'success';
}

export default function ProfileCompletionCard({ profileCompletion }) {
    // Hide the card once the profile is fully complete
    if (!profileCompletion || profileCompletion.percentage >= 100) {
        return null;
    }

    const { percentage, missing } = profileCompletion;

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 fw-bold">Complete Your Profile</h6>
                    <span className="text-muted small">{percentage}%</span>
                </div>
                <ProgressBar
                    now={percentage}
                    variant={getProgressVariant(percentage)}
                    className="mb-3"
                />
                {/* Checklist of incomplete steps with action buttons */}
                <ListGroup variant="flush">
                    {missing.map((key) => {
                        const step = STEP_CONFIG[key];
                        if (!step) return null;

                        return (
                            <ListGroup.Item
                                key={key}
                                className="d-flex justify-content-between align-items-center px-0 border-start-0 border-end-0"
                            >
                                <div>
                                    <div className="small fw-medium">
                                        {step.label}
                                    </div>
                                    <div className="text-muted small">
                                        {step.description}
                                    </div>
                                </div>
                                <Button
                                    as={Link}
                                    to={step.link}
                                    variant="outline-primary"
                                    size="sm"
                                    className="rounded-pill flex-shrink-0 ms-3"
                                >
                                    {step.linkText}
                                </Button>
                            </ListGroup.Item>
                        );
                    })}
                </ListGroup>
            </Card.Body>
        </Card>
    );
}

ProfileCompletionCard.propTypes = {
    profileCompletion: PropTypes.shape({
        percentage: PropTypes.number.isRequired,
        missing: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
};
